import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Building, Calendar, ChevronRight, Sparkles } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { NewSubscription } from '@/services/dashboard';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';

interface NewSubscriptionsWidgetProps {
  subscriptions: NewSubscription[];
  loading?: boolean;
}

const NewSubscriptionsWidget: React.FC<NewSubscriptionsWidgetProps> = ({
  subscriptions,
  loading = false
}) => {
  const navigate = useNavigate();



  const getCustomerName = (subscription: NewSubscription) => {
    const customer = subscription.subscription_customers?.[0]?.customer;
    return customer?.name || 'Cliente não identificado';
  };

  const getPlanName = (subscription: NewSubscription) => {
    const item = subscription.items?.[0];
    return item?.subscription_plan?.name || 'Plano não identificado';
  };

  const getTotalValue = (subscription: NewSubscription) => {
    return subscription.items?.reduce((total, item) => {
      return total + (item.unit_price * item.quantity);
    }, 0) || 0;
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm h-full">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
            <Sparkles className="h-4 w-4 text-green-500" />
            Novas Assinaturas (7 dias)
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[calc(100%-55px)] flex flex-col px-4 pb-4 pt-0">
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-extrabold">
          <Sparkles className="h-4 w-4 text-green-500" />
          Novas Assinaturas (7 dias)
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/subscriptions')}
          className="h-7 px-2"
        >
          Ver todas
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="h-[calc(100%-55px)] flex flex-col px-4 pb-4 pt-0">
        {subscriptions.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <Plus className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Nenhuma nova assinatura</p>
          </div>
        ) : (
          <div className="space-y-2 flex-1 overflow-y-auto">
            {subscriptions.slice(0, 5).map((subscription) => (
              <div
                key={subscription.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors min-h-[80px]"
                onClick={() => navigate(`/subscriptions/${subscription.id}`)}
              >
                <div className="flex-1 min-w-0">
                                     <div className="flex items-center gap-2 mb-1">
                     <p className="text-sm font-extrabold truncate">
                       {getCustomerName(subscription)}
                     </p>
                   </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                    <span className="font-extrabold">{getPlanName(subscription)}</span>
                    <span>•</span>
                    <span className="font-extrabold text-green-600">
                      {formatCurrency(getTotalValue(subscription))}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Criada em {format(parseISO(subscription.created_at), "dd/MM/yyyy", { locale: ptBR })}
                    </div>
                    <div className="flex items-center gap-1">
                      <Building className="h-3 w-3" />
                      ID: {subscription.display_id}
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
          </div>
        )}
        {subscriptions.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total de novas assinaturas:</span>
              <span className="font-extrabold">{subscriptions.length}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-muted-foreground">Valor total:</span>
              <span className="font-extrabold text-green-600">
                {formatCurrency(subscriptions.reduce((total, sub) => total + getTotalValue(sub), 0))}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NewSubscriptionsWidget;
